import React, { useState, useEffect } from 'react';
import {
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { 
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { Card } from './ui';
import { ProductService } from '../services/productService';
import { userService } from '../services/userService';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onDownload: (product: Product) => void;
  onRequireLogin?: () => void;
  onFavoriteAdded?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onDownload,
  onRequireLogin,
  onFavoriteAdded
}) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const productService = new ProductService();

  const userToken = userService.getAuthToken() || 'guest';
  const FAVORITES_CACHE_KEY = `artfulFavoriteProductIds:${userToken}`;

  const getCachedFavorites = (): Set<string> => {
    try {
      const raw = localStorage.getItem(FAVORITES_CACHE_KEY);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw));
    } catch {
      return new Set();
    }
  };

  const setCachedFavorites = (ids: Set<string>) => {
    try {
      localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify(Array.from(ids)));
    } catch {
      // best effort; ignore cache errors
    }
  };

  // Check if product is in favorites on component mount
  useEffect(() => {
    // Prefill from cache for immediate UI feedback
    const cached = getCachedFavorites();
    if (cached.has(product.id)) {
      setIsFavorite(true);
    }

    checkFavoriteStatus();
    // Dependencies intentionally limited to product.id to avoid re-fetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const checkFavoriteStatus = async () => {
    try {
      console.log('🔍 Checking favorite status for product:', product.id);
      const response = await productService.isFavorite(product.id);
      console.log('📥 isFavorite response:', response);
      if (response.success && response.data !== null && response.data !== undefined) {
        setIsFavorite(!!response.data);

        // Persist the latest server truth to cache for this user
        const cached = getCachedFavorites();
        if (response.data) {
          cached.add(product.id);
        } else {
          cached.delete(product.id);
        }
        setCachedFavorites(cached);
        console.log('💾 Cache updated after check, product is favorite:', response.data);
      }
    } catch (error) {
      console.error('❌ Error checking favorite status:', error);
    }
  };

  const handleToggleFavorite = async () => {
    // If user is not authenticated, show login dialog instead of alert
    if (!userService.isAuthenticated()) {
      if (onRequireLogin) {
        onRequireLogin();
      } else {
        alert('Please sign in to add items to your favorites.');
      }
      return;
    }

    if (isLoading) return;
    
    console.log('🎯 Toggle favorite for product:', product.id, 'Current state:', isFavorite);
    setIsLoading(true);
    try {
      if (isFavorite) {
        console.log('➖ Removing from favorites...');
        const response = await productService.removeFromFavorites(product.id);
        console.log('✅ Remove response:', response);
        setIsFavorite(false);
        const cached = getCachedFavorites();
        cached.delete(product.id);
        setCachedFavorites(cached);
      } else {
        console.log('➕ Adding to favorites...');
        // Optimistic update: turn heart red immediately
        setIsFavorite(true);
        try {
          const response = await productService.addToFavorites(product.id);
          console.log('✅ Add response:', response);
        } catch (error) {
          console.error('❌ Add failed, reverting:', error);
          // Revert if backend add fails
          setIsFavorite(false);
          throw error;
        }
        const cached = getCachedFavorites();
        cached.add(product.id);
        setCachedFavorites(cached);
        console.log('💾 Updated cache:', Array.from(cached));
        // Removed onFavoriteAdded callback to prevent navigation on heart icon click
      }
    } catch (error) {
      console.error('❌ Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      transition: 'transform 0.2s', 
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      '&:hover': { 
        transform: 'translateY(-2px)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        '& img': {
          transform: 'scale(1.05)'
        }
      } 
    }}
    onClick={() => navigate(`/listing/${product.id}`)}
    >
      <Box sx={{ 
        position: 'relative',
        overflow: 'hidden',
        height: 240,
        backgroundColor: '#f5f5f5'
      }}>
        <CardMedia
          component="img"
          height="240"
          image={product.image || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="16"%3ENo Image Available%3C/text%3E%3C/svg%3E'}
          alt={product.name}
          sx={{ 
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            transition: 'transform 0.5s ease-in-out',
            backgroundColor: '#f5f5f5' // Fallback background color
          }}
        />
        {/* Heart Icon for Favorites - Wrapped to prevent navigation */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            pointerEvents: 'auto'
          }}
        >
          <IconButton
            data-testid="favorite-icon"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleToggleFavorite();
            }}
            disabled={isLoading}
            sx={{
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
            '&:hover': { 
              bgcolor: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.1)'
            },
              transition: 'all 0.2s ease-in-out'
          }}
        >
          {isFavorite ? (
            <FavoriteIcon sx={{ color: '#e91e63' }} />
          ) : (
            <FavoriteBorderIcon sx={{ color: '#666' }} />
          )}
        </IconButton>
        </Box>
      </Box>
      
      <CardContent sx={{ p: 1.5 }}>
        {/* Product Title */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 400,
            fontSize: '0.875rem',
            mb: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.4,
            color: 'text.primary'
          }}
        >
          {product.name}
        </Typography>
        
        {/* Price - Prominently displayed */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            fontSize: '0.917rem', // 11px (9px + 2px) at 12px base
            mb: 1,
            color: 'text.primary',
            lineHeight: 1.2,
            textAlign: 'left'
          }}
        >
          ${product.price}
        </Typography>
        
        {/* Digital Download Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <CloudDownloadIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.917rem', // 11px (9px + 2px) at 12px base
              color: '#666',
              lineHeight: 1.2
            }}
          >
            Digital Download
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard; 